import React, { useEffect } from 'react';
import { Button, Card, Col, Row, Alert } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormProvider, RHFControl } from 'components/common/form';
import { useForm } from 'react-hook-form';
import { useBranchPaymentSettings, useUpdateBranchPaymentSettings } from 'hooks/useOrganisation';
import useBranchPermission from 'hooks/useBranchPermission';
import LoadingContainer from 'components/common/LoadingContainer';

const paymentSettingsScheme = Yup.object().shape({
  stripeKey: Yup.string().required('Required Field'),
  stripeSecret: Yup.string()
});

function PaymentSettings() {
  const { branchId } = useBranchPermission();
  const { paymentSettings, isLoading } = useBranchPaymentSettings();
  const { updatePaymentSettingsAsync, isUpdating } = useUpdateBranchPaymentSettings();

  const paymentSettingsResolver = yupResolver(paymentSettingsScheme);

  const methods = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      stripeKey: '',
      stripeSecret: ''
    },
    resolver: paymentSettingsResolver
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (paymentSettings) {
      reset({
        stripeKey: paymentSettings.stripe_key || '',
        stripeSecret: ''
      });
    }
  }, [paymentSettings, reset]);

  const onSubmit = async (data) => {
    try {
      const updateData = { stripe_key: data.stripeKey };
      if (data.stripeSecret) {
        updateData.stripe_secret = data.stripeSecret;
      }
      await updatePaymentSettingsAsync(updateData);
    } catch (error) {
      console.error('Error updating Stripe credentials:', error);
    }
  };

  if (!branchId) {
    return (
      <Card>
        <Card.Header className="fs-2">Payment Settings</Card.Header>
        <Card.Body>
          <Alert variant="warning">
            Please select a specific branch to configure payment settings.
            Each branch has its own Stripe credentials.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <Card>
      <Card.Header className="fs-2">Payment Settings</Card.Header>
      <Card.Body>
        <Alert variant="info" className="mb-3">
          These Stripe credentials are specific to the currently selected branch.
        </Alert>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={12}>
              <RHFControl
                label={'Stripe Publishable Key'}
                name={'stripeKey'}
                placeholder={'pk_live_... or pk_test_...'}
                required
              />
            </Col>
            <Col md={12} className="mt-2">
              <RHFControl
                label={'Stripe Secret Key'}
                name={'stripeSecret'}
                placeholder={paymentSettings?.id ? 'Enter new secret key to update' : 'sk_live_... or sk_test_...'}
                type="password"
              />
              {paymentSettings?.id && (
                <small className="text-muted">Secret key is stored securely. Leave blank to keep the current key.</small>
              )}
            </Col>
          </Row>
          <Button type="submit" className="mt-3 text-center" disabled={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save Payment Settings'}
          </Button>
        </FormProvider>
      </Card.Body>
    </Card>
  );
}

export default PaymentSettings;
